// SF1 + SF2 — velocity and conversion, one row per parent ASIN.
//
// Every row is a parent, not a child. Amazon counts a session against the
// parent detail page and the order against the child, so a child-level rate
// divides an order by sessions that were never attributed to it.
//
// The row carries two periods and marks which is which. Velocity, stock cover
// and direction follow the selected window. Sessions and conversion cannot:
// Amazon publishes per-ASIN traffic only aggregated across the report's whole
// coverage window, and there is no finer grain anywhere in SP-API.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../ProductPerformance.module.css';
import { ClippedWindow, formatRange } from '../../Sales/components/format';
import { getProducts } from '../services/productPerformanceApi';

// period 'coverage' marks a column measured over the whole report period.
//
// `value` is what the column sorts on, and it reads the same field the cell
// renders — a column that sorted on anything else would order the table by a
// number the reader cannot see.
const COLUMNS = [
  { key: 'asin', label: 'Parent ASIN', value: (row) => row.asin },
  { key: 'sessions', label: 'Sessions', numeric: true, period: 'coverage', value: (row) => row.sessions },
  { key: 'traffic_units', label: 'Units', numeric: true, period: 'coverage', value: (row) => row.units_ordered },
  { key: 'order_items', label: 'Order items', numeric: true, period: 'coverage', value: (row) => row.order_items },
  { key: 'unit_conv', label: 'Conv. (units)', numeric: true, period: 'coverage', value: (row) => row.unit_conversion },
  { key: 'order_conv', label: 'Conv. (orders)', numeric: true, period: 'coverage', value: (row) => row.order_conversion },
  { key: 'add_to_cart', label: 'Add to cart', numeric: true, period: 'coverage', value: (row) => row.add_to_cart?.rate },
  { key: 'units', label: 'Units sold', numeric: true, value: (row) => row.velocity?.units },
  { key: 'per_day', label: 'Velocity / day', numeric: true, value: (row) => row.velocity?.per_day },
  { key: 'per_week', label: 'Velocity / week', numeric: true, value: (row) => row.velocity?.per_week },
  { key: 'direction', label: 'Direction', numeric: true, value: (row) => row.velocity?.direction },
  { key: 'stock', label: 'Stock', numeric: true, value: (row) => row.velocity?.stock_on_hand },
  { key: 'cover', label: 'Days of cover', numeric: true, value: (row) => row.velocity?.days_of_cover },
  { key: 'lifecycle', label: 'Stage', value: (row) => row.lifecycle },
];

const BY_KEY = Object.fromEntries(COLUMNS.map((column) => [column.key, column]));

// Ordering that keeps unmeasured rows out of the way.
//
// A blank is not a zero here — an unmeasured velocity means the product could
// not be judged, not that it sold nothing — so blanks sink to the bottom in
// BOTH directions rather than winning the ascending sort. Sorting a catalogue
// by slowest and getting a page of products with no data would bury the answer.
const compare = (a, b, getValue, ascending) => {
  const left = getValue(a);
  const right = getValue(b);
  const leftBlank = left === null || left === undefined || left === '';
  const rightBlank = right === null || right === undefined || right === '';
  if (leftBlank || rightBlank) return leftBlank && rightBlank ? 0 : (leftBlank ? 1 : -1);
  if (typeof left === 'string' || typeof right === 'string') {
    return ascending
      ? String(left).localeCompare(String(right))
      : String(right).localeCompare(String(left));
  }
  return ascending ? left - right : right - left;
};

const PAGE_SIZE = 25;

// Cover this short is worth acting on before the next restock cycle.
const LOW_COVER_DAYS = 14;

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// Undefined is not zero. A product nobody visited has no conversion rate, and
// 0% would claim shoppers looked and refused.
const percent = (ratio) =>
  ratio === null || ratio === undefined ? <Blank /> : `${(ratio * 100).toFixed(2)}%`;

const decimal = (value, digits = 2) =>
  value === null || value === undefined ? <Blank /> : value.toFixed(digits);

// SF1. Velocity divides by the days the product could be bought where the
// stock history covers the window, and by calendar days where it does not.
// A product with no buyable day at all has no velocity — it was not slow, it
// was absent — and that reads as such rather than as a blank.
const VelocityCell = ({ velocity }) => {
  if (!velocity) return <Blank />;
  if (velocity.basis === 'buyable_days' && velocity.days === 0) {
    return <span className={local.coverLow}>out of stock</span>;
  }
  if (velocity.per_day === null || velocity.per_day === undefined) return <Blank />;
  return (
    <span title={`${velocity.units} units over ${velocity.days} ${
      velocity.basis === 'buyable_days' ? 'buyable' : 'calendar'} days`}>
      {velocity.per_day.toFixed(2)}
      {velocity.basis !== 'buyable_days' && (
        <span className={local.qualityEst} title="Calendar days: stock history does not cover this product for the whole window">
          cal
        </span>
      )}
    </span>
  );
};

// Which way velocity moved against the immediately prior period. Absent when
// that period sold too little to carry a direction, never rendered as flat.
const Direction = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const up = value > 0;
  return (
    <span className={up ? local.directionUp : local.directionDown}>
      {up ? '↑' : '↓'} {Math.abs(value * 100).toFixed(1)}%
    </span>
  );
};

// SF4. Nothing here is measured — Amazon publishes no cart data for this
// marketplace — so a value never appears without the label that says what it
// is. When no model is agreed the cell is blank and the reason sits once under
// the table rather than on every row.
const QUALITY_LABEL = { estimated: 'est.', fallback: 'default' };

const AddToCart = ({ value }) => {
  if (!value || value.rate === null || value.rate === undefined) {
    return <Blank />;
  }
  return (
    <span title={value.reason}>
      {(value.rate * 100).toFixed(2)}%
      <span className={value.quality === 'fallback' ? local.qualityFallback : local.qualityEst}>
        {QUALITY_LABEL[value.quality] || value.quality}
      </span>
    </span>
  );
};

// SF5. The label is provisional — its thresholds are not agreed — so a low
// confidence one is shown in a quieter voice than a clear one, and the reason
// is always available rather than leaving a bare verdict on the row.
const STAGE_CLASS = {
  launch: local.stageLaunch,
  growth: local.stageGrowth,
  maturity: local.stageMaturity,
  decline: local.stageDecline,
};

const Stage = ({ value }) => {
  if (!value?.stage) return <Blank />;
  const weak = value.confidence === 'low';
  return (
    <span
      className={`${local.stage} ${STAGE_CLASS[value.stage] || ''} ${weak ? local.stageWeak : ''}`}
      title={value.reason}
    >
      {value.stage}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ProductTable = ({ window: reportingWindow, onLoadingChange, onData }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [movingOnly, setMovingOnly] = useState(true);
  // null keeps the backend's own ordering (busiest first), which the copy above
  // the table describes. Sorting is something the reader opts into.
  const [sort, setSort] = useState(null);

  // First click sorts a numeric column high-to-low and a text one A–Z, because
  // that is the question being asked of each. A third click clears the sort
  // rather than cycling forever, so the default order is always reachable.
  const toggleSort = (column) => setSort((prev) => {
    const first = !column.numeric;
    if (!prev || prev.key !== column.key) return { key: column.key, ascending: first };
    if (prev.ascending === first) return { key: column.key, ascending: !first };
    return null;
  });

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getProducts(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Shared with the page's KPI strip, so the same figures are not fetched twice.
  useEffect(() => { onData?.(data); }, [data, onData]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  // Most of a long catalogue sells nothing in a short window, and a page of
  // empty velocities buries the products worth looking at.
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const kept = movingOnly ? all.filter((row) => row.velocity?.units > 0) : all;
    if (!sort) return kept;
    const column = BY_KEY[sort.key];
    if (!column) return kept;
    // Copied before sorting: the array belongs to `data`, and sorting it in
    // place would reorder the source every render.
    return [...kept].sort((a, b) => compare(a, b, column.value, sort.ascending));
  }, [data, movingOnly, sort]);

  useEffect(() => { setPage(1); }, [movingOnly, data, sort]);

  const pages = Math.max(Math.ceil(rows.length / PAGE_SIZE), 1);
  const visible = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Product behaviour</h2>
        {data?.coverage && (
          <span className={local.periodPill}>
            † {formatDate(data.coverage.start)} – {formatDate(data.coverage.end)}
          </span>
        )}
      </div>
      <p className={local.muted}>
        One row per parent ASIN, busiest first. Sessions and the two counts
        beside each rate are its denominator and numerators, so the percentage
        can be checked rather than trusted. Velocity, stock cover and direction
        describe the window you selected, divided by the days the product could
        actually be bought where its stock history is known and by calendar
        days (marked <span className={local.qualityEst}>cal</span>) where it is
        not. Columns marked{' '}
        <span className={local.colPeriod}>†</span> describe the whole report
        period in the tag above, because Amazon publishes per-product traffic
        only for that period and it cannot be narrowed.
      </p>
      <ClippedWindow data={data} />

      {/* SF1 at account level: the period and basis behind the strip's
          velocity tile. */}
      {data?.account_velocity?.per_day !== null && data?.account_velocity?.per_day !== undefined && (
        <div className={local.ratios}>
          <div>
            <div className={local.ratioLabel}>Catalogue velocity</div>
            <div className={local.ratioValue}>
              {data.account_velocity.per_day.toFixed(0)} units / day
            </div>
          </div>
          <div>
            <div className={local.ratioLabel}>Measured over</div>
            <div className={local.ratioValue}>
              {formatRange(data.effective_window?.start, data.effective_window?.end)}
            </div>
          </div>
          <div>
            <div className={local.ratioLabel}>On buyable days</div>
            <div className={local.ratioValue}>
              {inr.format(data.velocity_basis?.buyable_days ?? 0)} of {inr.format(data.products)}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the products'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <>
          <label className={local.toggle}>
            <input
              type="checkbox"
              checked={movingOnly}
              onChange={(e) => setMovingOnly(e.target.checked)}
            />
            Only products that sold in this window
          </label>

          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  {COLUMNS.map((column) => {
                    const active = sort?.key === column.key;
                    return (
                      <th key={column.key} className={column.numeric ? local.numeric : undefined}
                        aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" className={local.sortHead}
                          onClick={() => toggleSort(column)}
                          title={active ? 'Click to reverse, again to clear' : `Sort by ${column.label}`}>
                          {column.label}
                          {column.period === 'coverage' && (
                            <span className={local.colPeriod} title="Whole report period">†</span>
                          )}
                          <span className={active ? local.sortOn : local.sortOff} aria-hidden="true">
                            {active ? (sort.ascending ? '▲' : '▼') : '▲'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={COLUMNS.length}>
                      {loading ? 'Loading…' : 'No products sold in this window.'}
                    </td>
                  </tr>
                ) : visible.map((row) => {
                  const velocity = row.velocity || {};
                  const lowCover = velocity.days_of_cover !== null
                    && velocity.days_of_cover !== undefined
                    && velocity.days_of_cover <= LOW_COVER_DAYS;
                  return (
                    <tr key={row.asin}>
                      <td className={local.asinCell}>
                        {row.asin}
                        {row.child_count > 1 && (
                          <span className={local.familyTag}>{row.child_count} children</span>
                        )}
                      </td>
                      <td className={local.numeric}>{count(row.sessions)}</td>
                      <td className={local.numeric}>{count(row.units_ordered)}</td>
                      <td className={local.numeric}>{count(row.order_items)}</td>
                      <td className={local.numeric}>{percent(row.unit_conversion)}</td>
                      <td className={local.numeric}>{percent(row.order_conversion)}</td>
                      <td className={local.numeric}><AddToCart value={row.add_to_cart} /></td>
                      <td className={local.numeric}>{count(velocity.units)}</td>
                      <td className={local.numeric}><VelocityCell velocity={velocity} /></td>
                      <td className={local.numeric}>{decimal(velocity.per_week)}</td>
                      <td className={local.numeric}><Direction value={velocity.direction} /></td>
                      <td className={local.numeric}>{count(velocity.stock_on_hand)}</td>
                      <td className={`${local.numeric} ${lowCover ? local.coverLow : ''}`}>
                        {decimal(velocity.days_of_cover, 1)}
                      </td>
                      <td><Stage value={row.lifecycle} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length > PAGE_SIZE && (
            <div className={local.pager}>
              <button type="button" className={local.ghost}
                disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span className={local.muted}>
                Page {page} of {pages} · {inr.format(rows.length)} products
              </span>
              <button type="button" className={local.ghost}
                disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* SF5's thresholds are open questions, so the numbers actually applied
          are shown rather than hidden in the code. */}
      {data?.lifecycle?.provisional && (
        <div className={local.unavailable}>
          <div className={local.unavailableHead}>Stage is provisional</div>
          <div className={local.unavailableItem}>
            {data.lifecycle.reason} Applied here:{' '}
            {data.lifecycle.periods} periods of {data.lifecycle.period_days} days,
            a {(data.lifecycle.tolerance * 100).toFixed(0)}% band counted as
            stable, listings under {data.lifecycle.launch_max_listing_age_days} days
            old treated as Launch, and at least {data.lifecycle.min_units} units
            needed before a direction is called.
          </div>
        </div>
      )}

      {/* SF4's column is blank for a reason worth stating once. */}
      {data?.add_to_cart && !data.add_to_cart.configured && (
        <div className={local.unavailable}>
          <div className={local.unavailableHead}>Add to cart</div>
          <div className={local.unavailableItem}>{data.add_to_cart.reason}</div>
        </div>
      )}

      {/* Named rather than left as blank columns, so a reader knows the metric
          is missing by decision rather than by accident. */}
      {data?.unavailable?.length > 0 && (
        <div className={local.unavailable}>
          <div className={local.unavailableHead}>Not shown</div>
          {data.unavailable.map((item) => (
            <div key={item.metric} className={local.unavailableItem}>
              <strong>Lifecycle stage</strong>
              {' — '}{item.reason}
            </div>
          ))}
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ProductTable;
